import React, { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import TabContextMenu from './DashboardTabBar/TabContextMenu';
import CreateTabMenu from './DashboardTabBar/CreateTabMenu';
import TabItem from './DashboardTabBar/TabItem';
import { useTabRename } from './DashboardTabBar/useTabRename';
import { DashboardTabBarProps, ContextMenuState, CreateMenuState } from './DashboardTabBar/types';

const DashboardTabBar: React.FC<DashboardTabBarProps> = ({
  tabs,
  activeTabId,
  onTabSwitch,
  onTabCreate,
  onTabDelete,
  onTabRename,
  onTabReorder,
}) => {
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [createMenu, setCreateMenu] = useState<CreateMenuState | null>(null);
  const [dragState, setDragState] = useState<{
    draggedTabId: string | null;
    dragOverTabId: string | null;
    dropPosition: 'left' | 'right' | null;
  }>({ draggedTabId: null, dragOverTabId: null, dropPosition: null });

  const [tabSizing, setTabSizing] = useState<{
    maxTabWidth: number;
    activeTabWidth: number;
  }>({
    maxTabWidth: 200,
    activeTabWidth: 180,
  });
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const {
    renamingTabId,
    renameValue,
    renameInputRef,
    handleStartRename: startRename,
    handleFinishRename: finishRename,
    handleRenameKeyDown: renameKeyDown,
    setRenameValue,
  } = useTabRename();

  const handleTabContextMenu = (event: React.MouseEvent, tabId: string) => {
    event.preventDefault();
    event.stopPropagation();

    setContextMenu({
      tabId,
      position: { x: event.clientX, y: event.clientY },
    });
  };

  const handleCreateMenuOpen = (event: React.MouseEvent) => {
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    setCreateMenu({
      position: {
        x: rect.right,
        y: rect.bottom + 4,
      },
    });
  };

  const handleStartRename = (tabId: string) => {
    startRename(tabId, tabs);
    setContextMenu(null);
  };

  const handleFinishRename = () => {
    finishRename(onTabRename);
  };

  const handleRenameKeyDown = (event: React.KeyboardEvent) => {
    renameKeyDown(event, onTabRename);
  };

  const handleDuplicate = (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (tab) {
      if (tabId !== activeTabId) {
        // Switch to the target tab, then create copy from active tab
        onTabSwitch(tabId);
      }
      onTabCreate({ copyFromActiveTab: true, name: `Copy of ${tab.name}` });
    }
    setContextMenu(null);
  };

  const handleDelete = (tabId: string) => {
    onTabDelete(tabId);
    setContextMenu(null);
  };

  // Calculate dynamic tab sizing based on available space
  useEffect(() => {
    const calculateTabSizing = () => {
      if (!tabsContainerRef.current || tabs.length === 0) return;

      const container = tabsContainerRef.current;
      const containerWidth = container.offsetWidth;

      // Reserve space for plus button and padding
      const reservedSpace = 80;
      const availableWidth = containerWidth - reservedSpace;

      // Calculate ideal and minimum widths
      const idealTabWidth = 200;
      const minInactiveTabWidth = 60; // Very small for inactive tabs
      const minActiveTabWidth = 140; // Keep active tab readable
      const idealTotalWidth = tabs.length * idealTabWidth;

      let maxTabWidth = idealTabWidth;
      let activeTabWidth = idealTabWidth;

      // If we need to compress tabs
      if (idealTotalWidth > availableWidth) {
        const inactiveTabCount = tabs.length - 1;

        if (inactiveTabCount > 0) {
          // Active tab ALWAYS gets minimum readable width, no exceptions
          activeTabWidth = Math.max(
            minActiveTabWidth,
            Math.min(idealTabWidth, availableWidth * 0.6),
          );

          // Remaining space for inactive tabs
          const remainingWidth = availableWidth - activeTabWidth;
          const spacePerInactiveTab = remainingWidth / inactiveTabCount;

          // Inactive tabs can be very small
          maxTabWidth = Math.max(minInactiveTabWidth, Math.min(idealTabWidth, spacePerInactiveTab));

          // If inactive tabs would be impossibly small, just give them minimum and let it overflow
          if (maxTabWidth < minInactiveTabWidth) {
            maxTabWidth = minInactiveTabWidth;
          }
        } else {
          // Only one tab - give it reasonable space but never less than minimum
          activeTabWidth = Math.max(minActiveTabWidth, Math.min(idealTabWidth, availableWidth));
          maxTabWidth = activeTabWidth;
        }
      }

      setTabSizing({
        maxTabWidth: Math.floor(maxTabWidth),
        activeTabWidth: Math.floor(activeTabWidth),
      });
    };

    const timeoutId = setTimeout(calculateTabSizing, 0);

    window.addEventListener('resize', calculateTabSizing);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateTabSizing);
    };
  }, [tabs.length]);

  // Drag and drop handlers
  const handleDragStart = (tabId: string) => {
    setDragState({ draggedTabId: tabId, dragOverTabId: null, dropPosition: null });
  };

  const handleDragEnd = () => {
    // Always clear drag state when drag ends, regardless of drop success
    setTimeout(() => {
      setDragState({ draggedTabId: null, dragOverTabId: null, dropPosition: null });
    }, 50); // Small delay to allow drop to process first
  };

  const handleDragOver = (tabId: string, position: 'left' | 'right') => {
    if (dragState.draggedTabId && dragState.draggedTabId !== tabId) {
      setDragState({
        draggedTabId: dragState.draggedTabId,
        dragOverTabId: tabId,
        dropPosition: position,
      });
    }
  };

  const handleContainerDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragState.draggedTabId) return;

    const container = e.currentTarget as HTMLElement;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX;

    // Only check for beginning zone - end zone is handled by dedicated drop zone
    const isAtBeginning = mouseX < rect.left + 60;

    if (isAtBeginning) {
      setDragState({
        draggedTabId: dragState.draggedTabId,
        dragOverTabId: 'container-start',
        dropPosition: 'left',
      });
    }
  };

  const handleDrop = (targetTabId: string, position: 'left' | 'right') => {
    const { draggedTabId } = dragState;
    if (!draggedTabId) return;

    const fromIndex = tabs.findIndex((tab) => tab.id === draggedTabId);
    if (fromIndex === -1) return;

    let toIndex: number;

    // Handle special container drops
    if (targetTabId === 'container-start') {
      toIndex = 0;
    } else if (targetTabId === 'container-end' || targetTabId === 'drop-zone-end') {
      toIndex = tabs.length;
      // Adjust for removal of dragged item
      if (fromIndex < tabs.length) {
        toIndex = tabs.length - 1;
      }
    } else {
      // Handle normal tab drops
      const targetIndex = tabs.findIndex((tab) => tab.id === targetTabId);
      if (targetIndex === -1) return;

      toIndex = targetIndex;

      // If dropping to the right, insert after the target
      if (position === 'right') {
        toIndex = targetIndex + 1;
      }

      // Adjust for when dragged item is before target
      if (fromIndex < toIndex) {
        toIndex -= 1;
      }
    }

    if (fromIndex !== toIndex) {
      onTabReorder(fromIndex, toIndex);
    }

    // Clear drag state after drop
    setDragState({ draggedTabId: null, dragOverTabId: null, dropPosition: null });
  };

  const handleContainerDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const { dragOverTabId, dropPosition } = dragState;

    if (
      dragOverTabId === 'container-start' ||
      dragOverTabId === 'container-end' ||
      dragOverTabId === 'drop-zone-end'
    ) {
      handleDrop(dragOverTabId, dropPosition!);
    }
  };

  return (
    <>
      <div className="flex items-end bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 pt-1 pb-0">
        <div
          ref={tabsContainerRef}
          className="flex items-end flex-1 min-w-0 -mb-px relative gap-px"
          onDragOver={handleContainerDragOver}
          onDrop={handleContainerDrop}
        >
          {/* Start drop zone indicator */}
          {dragState.dragOverTabId === 'container-start' && (
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#E34290] z-30"></div>
          )}
          {tabs.map((tab) => (
            <TabItem
              key={tab.id}
              tab={tab}
              isRenaming={renamingTabId === tab.id}
              renameValue={renameValue}
              onTabSwitch={onTabSwitch}
              onTabContextMenu={handleTabContextMenu}
              onTabDelete={onTabDelete}
              onRenameValueChange={setRenameValue}
              onRenameFinish={handleFinishRename}
              onRenameKeyDown={handleRenameKeyDown}
              renameInputRef={renameInputRef}
              canDelete={tabs.length > 1}
              isDragging={dragState.draggedTabId === tab.id}
              dropPosition={dragState.dragOverTabId === tab.id ? dragState.dropPosition : null}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              maxWidth={tab.isActive ? tabSizing.activeTabWidth : tabSizing.maxTabWidth}
            />
          ))}

          {/* Dedicated end drop zone */}
          <div
            className={`
              flex-shrink-0 transition-all duration-200 -mb-px
              ${dragState.draggedTabId ? 'w-8' : 'w-0'}
              ${dragState.dragOverTabId === 'drop-zone-end' ? 'bg-[#E34290] opacity-50' : ''}
            `}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (dragState.draggedTabId) {
                setDragState({
                  draggedTabId: dragState.draggedTabId,
                  dragOverTabId: 'drop-zone-end',
                  dropPosition: 'right',
                });
              }
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDrop('drop-zone-end', 'right');
            }}
          >
            {dragState.dragOverTabId === 'drop-zone-end' && (
              <div className="w-1 bg-[#E34290] h-full"></div>
            )}
          </div>
        </div>

        <button
          onClick={handleCreateMenuOpen}
          className="ml-3 mb-2 p-1.5 text-gray-500 dark:text-gray-400 rounded-full focus:outline-none"
          title="Create new dashboard"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {contextMenu && (
        <TabContextMenu
          isOpen={true}
          onClose={() => setContextMenu(null)}
          onRename={() => handleStartRename(contextMenu.tabId)}
          onDuplicate={() => handleDuplicate(contextMenu.tabId)}
          onDelete={() => handleDelete(contextMenu.tabId)}
          position={contextMenu.position}
          canDelete={tabs.length > 1}
        />
      )}

      {createMenu && (
        <CreateTabMenu
          isOpen={true}
          onClose={() => setCreateMenu(null)}
          onCreate={onTabCreate}
          position={createMenu.position}
        />
      )}
    </>
  );
};

export default DashboardTabBar;
